import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Parses lesson body HTML with embedded exercise shortcodes and renders
 * saveable inputs inline. Supported shortcodes:
 *   [[writing key="unique-key" label="Label"]]
 *   [[ledger key="unique-key" left="what respectability gave you" right="what it cost you"]]
 *
 * Persists per-user via /api/exercises. Auto-saves 700ms after last change.
 */
/**
 * Shortcode grammar (case-insensitive, forgiving):
 *   [[writing key="..." label="..."]]
 *   [[ledger key="..." left="..." right="..."]]
 * Also accepts curly/smart quotes ("" ''), missing attributes, and whitespace/newlines
 * inside the shortcode. If the admin's editor autocorrected quotes, it still parses.
 */
const SHORTCODE_RE = /\[\[\s*(writing|ledger|log)\b([^\]]*)\]\]/gi;

function normalizeQuotes(s) {
  return (s || "")
    .replace(/[\u201C\u201D\u201E\u2033]/g, '"')  // curly double
    .replace(/[\u2018\u2019\u201A\u2032]/g, "'"); // curly single
}

function parseAttrs(s) {
  const clean = normalizeQuotes(s);
  const out = {};
  // key="value" or key='value' or key=value
  const re = /(\w+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s\]]+))/g;
  let m;
  while ((m = re.exec(clean))) {
    out[m[1].toLowerCase()] = m[2] ?? m[3] ?? m[4] ?? "";
  }
  return out;
}

function splitBody(html) {
  const parts = [];
  let last = 0;
  SHORTCODE_RE.lastIndex = 0;
  let m;
  while ((m = SHORTCODE_RE.exec(html))) {
    if (m.index > last) parts.push({ type: "html", value: html.slice(last, m.index) });
    parts.push({ type: m[1].toLowerCase(), attrs: parseAttrs(m[2] || "") });
    last = m.index + m[0].length;
  }
  if (last < html.length) parts.push({ type: "html", value: html.slice(last) });
  return parts;
}

export default function ExerciseBody({ html, lessonId, unlocked, previewMode }) {
  const parts = useMemo(() => splitBody(html || ""), [html]);
  const hasShortcodes = parts.some((p) => p.type !== "html");
  const [values, setValues] = useState({});
  const timers = useRef({});

  useEffect(() => {
    if (!lessonId || !unlocked || !hasShortcodes || previewMode) return;
    api.get(`/exercises/${lessonId}`).then((r) => setValues(r.data || {})).catch(() => {});
  }, [lessonId, unlocked, hasShortcodes, previewMode]);

  const save = (key, payload) => {
    setValues((v) => ({ ...v, [key]: { ...(v[key] || {}), ...payload } }));
    if (previewMode) return;
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(async () => {
      try {
        await api.post("/exercises", { lesson_id: lessonId, key, ...payload });
      } catch (e) {
        toast.error("save failed");
      }
    }, 700);
  };

  if (!hasShortcodes) {
    return <div className="lesson-prose" dangerouslySetInnerHTML={{ __html: html || "" }} />;
  }

  return (
    <div className="lesson-prose">
      {parts.map((p, i) => {
        if (p.type === "html") return <div key={i} dangerouslySetInnerHTML={{ __html: p.value }} />;
        if (p.type === "writing") {
          const key = p.attrs.key || `writing-${i}`;
          const label = p.attrs.label || "your notes";
          const value = values[key]?.value || "";
          return (
            <div key={i} className="my-8" data-testid={`exercise-writing-${key}`}>
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--gold)" }}>{label}</p>
              <textarea
                rows={6}
                value={value}
                onChange={(e) => save(key, { value: e.target.value })}
                placeholder="write here."
                className="w-full"
                style={{
                  background: "var(--cream)",
                  border: "1px solid var(--gold-soft)",
                  padding: "1rem",
                  fontFamily: "'Inter', sans-serif",
                  color: "var(--ink)",
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  borderRadius: "2px",
                }}
                data-testid={`exercise-writing-input-${key}`}
              />
              <p className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>saved automatically to your account.</p>
            </div>
          );
        }
        if (p.type === "ledger") {
          const key = p.attrs.key || "ledger";
          const leftLabel = p.attrs.left || "column a";
          const rightLabel = p.attrs.right || "column b";
          const current = values[key] || { left: [], right: [] };
          const setSide = (side, arr) => save(key, { ledger: { left: side === "left" ? arr : current.left || [], right: side === "right" ? arr : current.right || [] } });
          return (
            <LedgerInput
              key={i}
              label={{ left: leftLabel, right: rightLabel }}
              value={current}
              onChange={setSide}
              testKey={key}
            />
          );
        }
        if (p.type === "log") {
          const key = p.attrs.key || `log-${i}`;
          const label = p.attrs.label || "log";
          const entryLabel = p.attrs.entrylabel || p.attrs.entryLabel || "new entry";
          const dated = String(p.attrs.dated ?? "true").toLowerCase() !== "false";
          return (
            <LogInput
              key={i}
              testKey={key}
              lessonId={lessonId}
              label={label}
              entryLabel={entryLabel}
              dated={dated}
              previewMode={previewMode}
            />
          );
        }
        return null;
      })}
    </div>
  );
}

function LedgerInput({ label, value, onChange, testKey }) {  const left = value.left || [];
  const right = value.right || [];
  const setLeft = (arr) => onChange("left", arr);
  const setRight = (arr) => onChange("right", arr);

  return (
    <div className="my-8" data-testid={`exercise-ledger-${testKey}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { arr: left, set: setLeft, lbl: label.left, side: "left" },
          { arr: right, set: setRight, lbl: label.right, side: "right" },
        ].map((col) => (
          <div key={col.side} style={{ border: "1px solid var(--gold-soft)", padding: "1rem", borderRadius: "2px", background: "var(--cream)" }}>
            <p className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--gold)" }}>{col.lbl}</p>
            <ul className="space-y-2 mb-3">
              {col.arr.map((v, idx) => (
                <li key={idx} className="flex gap-2">
                  <input
                    value={v}
                    onChange={(e) => {
                      const copy = [...col.arr];
                      copy[idx] = e.target.value;
                      col.set(copy);
                    }}
                    className="flex-1"
                    style={{
                      background: "transparent",
                      borderBottom: "1px solid var(--gold-soft)",
                      padding: "0.4rem 0",
                      fontFamily: "'Inter', sans-serif",
                      color: "var(--ink)",
                      fontSize: "0.95rem",
                      outline: "none",
                    }}
                    data-testid={`ledger-${testKey}-${col.side}-${idx}`}
                  />
                  <button
                    type="button"
                    className="text-xs opacity-50 hover:opacity-100"
                    onClick={() => col.set(col.arr.filter((_, i) => i !== idx))}
                    style={{ color: "var(--ink-soft)" }}
                    title="remove"
                  >×</button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => col.set([...col.arr, ""])}
              className="text-xs underline"
              style={{ color: "var(--gold)" }}
              data-testid={`ledger-${testKey}-add-${col.side}`}
            >+ add a line</button>
          </div>
        ))}
      </div>
      <p className="text-xs mt-3" style={{ color: "var(--ink-soft)" }}>saved automatically to your account.</p>
    </div>
  );
}

function LogInput({ testKey, lessonId, label, entryLabel, dated, previewMode }) {
  const [entries, setEntries] = useState([]);
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!lessonId || previewMode) return;
    api.get(`/exercises/${lessonId}`).then((r) => {
      const node = (r.data || {})[testKey] || {};
      setEntries(node.entries || []);
    }).catch(() => {});
  }, [lessonId, testKey, previewMode]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  const addEntry = async () => {
    const t = text.trim();
    if (!t) return;
    if (previewMode) {
      setEntries((xs) => [{ id: `demo-${Date.now()}`, text: t, date: new Date().toISOString() }, ...xs]);
      setText("");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/exercises/log/add", { lesson_id: lessonId, key: testKey, text: t, dated });
      setEntries(data.entries || []);
      setText("");
    } catch (e) {
      toast.error("could not add entry");
    } finally { setBusy(false); }
  };

  const startEdit = (e) => { setEditingId(e.id); setEditingText(e.text); };
  const cancelEdit = () => { setEditingId(null); setEditingText(""); };
  const saveEdit = async () => {
    const t = editingText.trim();
    if (!t) return;
    if (previewMode) {
      setEntries((xs) => xs.map((x) => x.id === editingId ? { ...x, text: t } : x));
      cancelEdit();
      return;
    }
    try {
      const { data } = await api.post("/exercises/log/edit", { lesson_id: lessonId, key: testKey, entry_id: editingId, text: t });
      setEntries(data.entries || []);
      cancelEdit();
    } catch (e) { toast.error("could not save edit"); }
  };
  const remove = async (id) => {
    if (!window.confirm("delete this entry?")) return;
    if (previewMode) {
      setEntries((xs) => xs.filter((x) => x.id !== id));
      return;
    }
    try {
      const { data } = await api.post("/exercises/log/delete", { lesson_id: lessonId, key: testKey, entry_id: id });
      setEntries(data.entries || []);
    } catch (e) { toast.error("could not delete"); }
  };

  // newest first
  const sorted = [...entries].sort((a, b) => (b.date || b.updated_at || "").localeCompare(a.date || a.updated_at || ""));

  return (
    <div className="my-8" data-testid={`exercise-log-${testKey}`}>
      <h2 style={{
        fontFamily: "'Playfair Display', serif",
        color: "var(--ink)",
        fontWeight: 500,
        fontSize: "1.6rem",
        marginTop: "2rem",
        marginBottom: "1rem",
        lineHeight: 1.2,
      }}>{label}</h2>

      <div style={{ background: "var(--cream)", border: "1px solid var(--gold-soft)", padding: "1rem", borderRadius: "2px" }}>
        <div className="flex gap-2 items-end mb-3">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{entryLabel}</label>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addEntry(); }}
              placeholder="write today's entry."
              className="w-full"
              style={{
                background: "transparent",
                borderBottom: "1px solid var(--gold-soft)",
                padding: "0.5rem 0",
                fontFamily: "'Inter', sans-serif",
                color: "var(--ink)",
                fontSize: "1rem",
                outline: "none",
              }}
              data-testid={`log-input-${testKey}`}
            />
          </div>
          <button
            type="button"
            onClick={addEntry}
            disabled={busy || !text.trim()}
            className="btn-primary"
            data-testid={`log-add-${testKey}`}
          >add entry</button>
        </div>
        {sorted.length === 0 ? (
          <p className="text-xs" style={{ color: "var(--ink-soft)" }}>no entries yet. write one above.</p>
        ) : (
          <ul className="space-y-3 max-h-96 overflow-y-auto sidebar-scroll pr-2" data-testid={`log-entries-${testKey}`}>
            {sorted.map((e) => (
              <li key={e.id} className="border-l-2 pl-3 py-1" style={{ borderColor: "var(--gold)" }}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest" style={{ color: "var(--gold)" }}>
                    {dated ? fmtDate(e.date || e.updated_at) : "entry"}
                  </span>
                  {editingId !== e.id && (
                    <div className="flex gap-2 text-xs">
                      <button onClick={() => startEdit(e)} className="underline opacity-60 hover:opacity-100" style={{ color: "var(--ink-soft)" }} data-testid={`log-edit-${testKey}-${e.id}`}>edit</button>
                      <button onClick={() => remove(e.id)} className="underline opacity-60 hover:opacity-100" style={{ color: "var(--ink-soft)" }} data-testid={`log-delete-${testKey}-${e.id}`}>delete</button>
                    </div>
                  )}
                </div>
                {editingId === e.id ? (
                  <div className="space-y-2">
                    <textarea
                      rows={3}
                      value={editingText}
                      onChange={(ev) => setEditingText(ev.target.value)}
                      className="w-full"
                      style={{
                        background: "var(--cream-2)",
                        border: "1px solid var(--gold-soft)",
                        padding: "0.5rem",
                        fontFamily: "'Inter', sans-serif",
                        color: "var(--ink)",
                        fontSize: "0.95rem",
                        borderRadius: "2px",
                      }}
                      data-testid={`log-edit-input-${testKey}-${e.id}`}
                    />
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="btn-primary text-xs" data-testid={`log-save-${testKey}-${e.id}`}>save</button>
                      <button onClick={cancelEdit} className="btn-ghost text-xs" data-testid={`log-cancel-${testKey}-${e.id}`}>cancel</button>
                    </div>
                  </div>
                ) : (
                  <p className="text-base leading-relaxed whitespace-pre-wrap" style={{ color: "var(--ink)" }}>{e.text}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs mt-2" style={{ color: "var(--ink-soft)" }}>every entry is kept, timestamped, and saved to your account.</p>
    </div>
  );
}
